import React, { MutableRefObject, useEffect, useState } from 'react';
import { Drawer, SolidButton } from '@noths/polaris-client-ribbons-design-system';
import { priceFormatter } from '@noths/polaris-client-utils';
import { CurrencyCode } from '@noths/polaris-dev-ts-types';

import {
  InteractionErrorTrackingEvent,
  InteractionTrackingEvent,
  ProgressTrackingEvent,
  TrackedEvents,
} from 'src/redux/personalisation';
import { PriceModifier, ProductOption, ProductOptionValue } from 'src/types';
import * as copy from '../../constants/copy';
import { getFieldPosition, getProgressMilestone } from '../../utils';
import { ToggleButton } from '../ToggleButton/ToggleButton';
import * as styles from '../../styles/Personalisation.styles';

export const SELECTION_MULTIPLE = 'selection-multiple';
export const CTA_APPLY_CLASSNAME = 'cta-apply-button';

export const DRAWER_DESCRIPTOR_ID = 'drawer-description';
const TOGGLE_BUTTON_GROUP_DESCRIPTOR_ID = 'personalisation-toggle-button-group-description';

const getAmount = (priceModifier: PriceModifier | null | undefined, currencyCode: CurrencyCode) => {
  return priceModifier?.prices.find(({ currency }) => currency === currencyCode)?.amount ?? 0;
};

export interface SelectionOptionValueProps {
  appliedValue: ProductOptionValue | undefined;
  currency: CurrencyCode;
  elementId: string;
  isExpanded: boolean;
  option: ProductOption;
  options: ProductOption[];
  target: MutableRefObject<HTMLUListElement>;
  totalOutstandingSelections: number;
  totalPrice: number;
  totalRequiredPersonalisations: number;
  trackedEvents: TrackedEvents;
  onClose: () => void;
  onConfirm: (selectedValue: ProductOptionValue | null) => void;
}

export interface DispatchProps {
  trackInteraction: (payload: InteractionTrackingEvent) => void;
  trackInteractionError: (payload: InteractionErrorTrackingEvent) => void;
  trackProgressMilestone: (payload: ProgressTrackingEvent) => void;
}

export const MultipleOptionSelectionDrawer = ({
  appliedValue,
  currency,
  elementId,
  isExpanded,
  option,
  options,
  target,
  totalOutstandingSelections,
  totalPrice,
  totalRequiredPersonalisations,
  trackedEvents,
  onClose,
  onConfirm,
  trackInteraction,
  trackProgressMilestone,
}: SelectionOptionValueProps & DispatchProps) => {
  const [width, setWidth] = useState(0);
  const [selectedValue, setSelectedValue] = useState<ProductOptionValue | null>(
    appliedValue || null,
  );
  const selectedAmount = getAmount(selectedValue?.priceModifier, currency);
  const appliedAmount = getAmount(appliedValue?.priceModifier, currency);
  const appliedPrice = totalPrice - appliedAmount + selectedAmount;

  useEffect(() => {
    const updateWidth = () => {
      const leftPos = target.current.getBoundingClientRect().left;
      const currentWidth = window.innerWidth - leftPos!;
      setWidth(currentWidth);
    };

    updateWidth();

    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Only used if value prop gets updated to refresh selectedValue
  useEffect(() => {
    if (appliedValue && appliedValue !== selectedValue) {
      setSelectedValue(appliedValue);
    }
  }, [appliedValue]);

  const handleOnChange = (newValue: ProductOptionValue | null, currentValueName: string) => {
    const fieldName = option.name;
    const fieldPosition = getFieldPosition(options, option);

    trackInteraction({
      field_position: fieldPosition,
      field_name: fieldName,
      field_action: 'Field Input',
      field_type: '3 and more values selection',
      field_value: newValue ? currentValueName : null,
      required_field: option.required,
    });

    const milestone = getProgressMilestone({
      totalRequiredPersonalisations,
      totalOutstandingSelections,
      trackedEvents,
      fieldName,
      isRequiredPersonalisation: option.required,
    });

    if (milestone) {
      trackProgressMilestone({
        field_position: fieldPosition,
        field_name: fieldName,
        field_action: milestone,
        field_type: '3 and more values selection',
        field_value: currentValueName,
        required_field: true,
      });
    }

    setSelectedValue(newValue);
  };

  const handleFocusTracking = (valueName: string) => {
    if (trackedEvents.focus[option.name]) {
      return;
    }

    trackInteraction({
      field_position: getFieldPosition(options, option),
      field_name: option.name,
      field_action: 'Field Focus',
      field_type: '3 and more values selection',
      field_value: valueName,
      required_field: option.required,
    });
  };

  const handleOnClose = () => {
    setSelectedValue(appliedValue || null);
    onClose();
  };

  const handleOnConfirm = () => {
    onConfirm(selectedValue);
  };

  return (
    <Drawer
      isOpen={isExpanded}
      onRequestClose={handleOnClose}
      onOverlayClick={handleOnConfirm}
      contentLabel={copy.CONTENT_LABEL}
      describedBy={DRAWER_DESCRIPTOR_ID}
    >
      <section css={styles.selectionMultipleDrawer} style={{ width }} id={elementId}>
        <div css={styles.selectionMultipleInner}>
          <div css={styles.topModalSection}>
            <button
              css={[styles.inputSelect, styles.inputClose]}
              onClick={handleOnClose}
              aria-expanded={isExpanded}
              aria-controls={elementId}
              aria-label="Close"
            />
            {option.required && <p css={styles.requiredLabel}>{copy.REQUIRED_COPY}</p>}
          </div>
          <p
            id={DRAWER_DESCRIPTOR_ID}
            css={[styles.legend, styles.legendLarge]}
            dangerouslySetInnerHTML={{ __html: option.name }}
          />
          <div
            css={styles.innerFieldset}
            role="group"
            aria-labelledby={DRAWER_DESCRIPTOR_ID}
            aria-describedby={TOGGLE_BUTTON_GROUP_DESCRIPTOR_ID}
          >
            <p css={styles.toggleButtonGroupDescription} id={TOGGLE_BUTTON_GROUP_DESCRIPTOR_ID}>
              {copy.TOGGLE_BUTTON_GROUP_DESCRIPTION}
            </p>
            {option.values?.map(({ id, name, priceModifier }) => (
              <ToggleButton
                currency={currency}
                onClick={(newValue) => handleOnChange(newValue, name)}
                onFocus={() => handleFocusTracking(name)}
                key={id}
                value={{ id, name, priceModifier }}
                isSelected={id === selectedValue?.id}
              />
            ))}
          </div>
          <div css={styles.ctaApplyButtonWrapper}>
            <p css={styles.priceApplyText}>
              {copy.AFTER_APPLYING_LABEL} {priceFormatter(currency, appliedPrice)}
            </p>
            <SolidButton
              className={CTA_APPLY_CLASSNAME}
              css={styles.ctaApplyButton}
              onClick={handleOnConfirm}
              fullWidth
            >
              {copy.APPLY_COPY}
            </SolidButton>
          </div>
        </div>
      </section>
    </Drawer>
  );
};
